// =============================================
// PÁGINA: SIMULADOR DE FLUXOS
// =============================================
// Arquivo: FlowSimulatorPage.tsx
// Descrição: Página para testar e simular fluxos de automação
// =============================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import FlowSimulator from '../../components/Automation/FlowSimulator';
import './AutomationList.css';

// =============================================
// INTERFACES
// =============================================

interface AutomationFlow {
  id: number;
  nome: string;
  descricao?: string;
  ativo: boolean;
  trigger_type: string;
  flow_data?: {
    nodes: any[];
    edges: any[];
  };
  criado_em: string;
  atualizado_em: string;
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const FlowSimulatorPage: React.FC = () => {
  const navigate = useNavigate();
  const [flows, setFlows] = useState<AutomationFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<AutomationFlow | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // =============================================
  // CARREGAMENTO DE DADOS
  // =============================================

  useEffect(() => {
    loadFlows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFlows = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = { ativo: true }; // Apenas fluxos ativos
      if (searchTerm) params.search = searchTerm;

      const response = await apiService.listAutomationFlows(params);

      if (response.success && response.data) {
        setFlows(response.data);
      } else {
        setError('Erro ao carregar fluxos de automação');
      }
    } catch (err) {
      console.error('Erro ao carregar fluxos:', err);
      setError('Erro ao carregar fluxos de automação');
    } finally {
      setLoading(false);
    }
  };

  // =============================================
  // HANDLERS
  // =============================================

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadFlows();
  };

  const handleSelectFlow = async (flow: AutomationFlow) => {
    try {
      // Carregar dados completos do fluxo se necessário
      if (!flow.flow_data) {
        const response = await apiService.getAutomationFlow(flow.id);
        if (response.success && response.data) {
          setSelectedFlow(response.data);
        } else {
          alert('Erro ao carregar dados do fluxo');
        }
      } else {
        setSelectedFlow(flow);
      }
    } catch (err) {
      console.error('Erro ao carregar fluxo:', err);
      alert('Erro ao carregar fluxo');
    }
  };

  const handleCloseSimulator = () => {
    setSelectedFlow(null);
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  // =============================================
  // RENDERIZAÇÃO - SIMULADOR
  // =============================================

  if (selectedFlow) {
    return (
      <FlowSimulator
        flowId={selectedFlow.id}
        flowData={selectedFlow.flow_data || { nodes: [], edges: [] }}
        onClose={handleCloseSimulator}
      />
    );
  }

  // =============================================
  // RENDERIZAÇÃO - LISTA DE FLUXOS
  // =============================================

  return (
    <div className="automation-list-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={handleBack}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <div>
            <h1>Simulador de Fluxos</h1>
            <p>Selecione um fluxo para testar e simular seu comportamento</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters-section">
        <form className="search-form" onSubmit={handleSearch}>
          <div className="search-input-group">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar fluxos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary">
            <i className="fas fa-search"></i>
            Buscar
          </button>
        </form>
      </div>

      {/* Lista de Fluxos */}
      <div className="flows-list">
        {loading && (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Carregando fluxos...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
            <button onClick={loadFlows} className="btn-secondary">
              Tentar Novamente
            </button>
          </div>
        )}

        {!loading && !error && flows.length === 0 && (
          <div className="empty-state">
            <i className="fas fa-robot"></i>
            <h3>Nenhum fluxo ativo encontrado</h3>
            <p>Crie e ative fluxos no Editor de Fluxos para simulá-los aqui</p>
            <button
              onClick={() => navigate('/automacoes')}
              className="btn-primary"
            >
              <i className="fas fa-plus"></i>
              Ir para Editor de Fluxos
            </button>
          </div>
        )}

        {!loading && !error && flows.length > 0 && (
          <div className="flows-grid">
            {flows.map((flow) => (
              <div
                key={flow.id}
                className="flow-card simulator-card"
                onClick={() => handleSelectFlow(flow)}
              >
                <div className="flow-card-header">
                  <div className="flow-icon">
                    <i className="fas fa-flask"></i>
                  </div>
                  <div className="flow-info">
                    <h3>{flow.nome}</h3>
                    {flow.descricao && <p>{flow.descricao}</p>}
                  </div>
                </div>

                <div className="flow-card-meta">
                  <span className="flow-trigger">
                    <i className="fas fa-bolt"></i>
                    {flow.trigger_type === 'keyword' ? 'Palavra-chave' :
                     flow.trigger_type === 'incoming' ? 'Mensagem recebida' :
                     flow.trigger_type === 'schedule' ? 'Agendado' :
                     'Manual'}
                  </span>
                  <span className="flow-status active">
                    <i className="fas fa-check-circle"></i>
                    Ativo
                  </span>
                </div>

                <div className="flow-card-footer">
                  <button className="btn-simulate">
                    <i className="fas fa-play"></i>
                    Simular Fluxo
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowSimulatorPage;
